/**
 * PRODUCT GRID CUSTOM ELEMENT
 *
 * Handles product filtering and sorting with URL-based state management for both collections and search.
 * Provides soft reload functionality - updates URL and content without full page refresh.
 *
 * Usage:
 *   <x-product-grid data-context="collection">
 *     <!-- Product toolbar, filters, and grid content -->
 *   </x-product-grid>
 *
 *   <x-product-grid data-context="search">
 *     <!-- Product toolbar, filters, and grid content -->
 *   </x-product-grid>
 *
 * Features:
 * - Context-aware (collection or search)
 * - Sorting via data-sort-button elements
 * - Filtering via data-filter-input elements
 * - URL parameter management (sort_by, filters, q for search)
 * - Soft reload with history API
 * - Active state management for sort buttons and filters
 * - Price range slider support
 */

import { formatMoney } from '@/components/helpers/currency-formatter'

export class ProductGridElement extends HTMLElement {
  private sortButtons: NodeListOf<HTMLInputElement> | null = null
  private filterInputs: NodeListOf<HTMLInputElement> | null = null
  private gridContainer: HTMLElement | null = null
  private currentSortValue: string = 'manual'
  private currentFilters: Record<string, string[]> = {}
  private isLoading: boolean = false
  private debounceTimer: number | null = null
  private isUpdatingFromServer: boolean = false
  private hasQueuedUpdate: boolean = false
  private disabledFilterStates: Map<string, boolean> = new Map()
  private lastSizeButtonClickTime: number = 0
  private sizeButtonCooldown: number = 1000 // 1 second cooldown

  private context: 'collection' | 'search' = 'collection'

  static SORT_BUTTON_SELECTOR = '[data-sort-button]'
  static FILTER_INPUT_SELECTOR = '[data-filter-input]'
  static SORT_LABELS: Record<string, string> = {
    manual: 'Recommended',
    'created-descending': 'Newest',
    'title-ascending': 'Name: A - Z',
    'title-descending': 'Name: Z - A',
    'price-ascending': 'Price: Low to High',
    'price-descending': 'Price: High to Low'
  }

  connectedCallback() {
    this.initializeContext()
    this.initializeElements()
    this.initializeFromURL()
    this.addEventListeners()
    this.initializePriceSliders()
    this.initializeUniversalSizeFilters()
    this.injectPriceRangeButton()
    this.updateActiveFiltersWithPreferredSizes()
  }

  disconnectedCallback() {
    this.removeEventListeners()
  }

  /**
   * Initialize context from data attributes
   */
  private initializeContext() {
    this.context = this.getAttribute('data-context') as 'collection' | 'search' || 'collection'

    if (!this.getAttribute('data-context') && window.location.pathname.includes('/search')) {
      this.context = 'search'
    }
  }

  /**
   * Initialize DOM element references
   */
  private initializeElements() {
    this.sortButtons = document.querySelectorAll(ProductGridElement.SORT_BUTTON_SELECTOR)
    this.filterInputs = this.querySelectorAll(ProductGridElement.FILTER_INPUT_SELECTOR)
    this.gridContainer = this.querySelector('[data-product-grid-container]') as HTMLElement
  }

  /**
   * Initialize component state from URL parameters
   */
  private initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search)
    const sortBy = urlParams.get('sort_by') || 'manual'

    this.currentFilters = {}
    for (const [key, value] of urlParams.entries()) {
      if (key !== 'sort_by' && key !== 'q' && key !== 'page') {
        if (!this.currentFilters[key]) {
          this.currentFilters[key] = []
        }
        this.currentFilters[key].push(value)
      }
    }

    this.currentSortValue = sortBy
    this.updateActiveButton(sortBy)
    this.updateActiveFilters()
  }

  /**
   * Attach event listeners for sorting, filtering, and navigation
   */
  private addEventListeners() {
    this.sortButtons?.forEach(button => {
      button.addEventListener('change', this.handleSortChange.bind(this))
    })

    this.filterInputs?.forEach(input => {
      if (input.type !== 'range') {
        input.addEventListener('change', this.handleFilterChange.bind(this))
      }
    })

    this.addEventListener('click', this.handleActiveFilterClick.bind(this))

    window.addEventListener('popstate', this.handlePopState.bind(this))
  }

  /**
   * Remove all event listeners
   */
  private removeEventListeners() {
    this.sortButtons?.forEach(button => {
      button.removeEventListener('change', this.handleSortChange.bind(this))
    })

    this.filterInputs?.forEach(input => {
      if (input.type !== 'range') {
        input.removeEventListener('change', this.handleFilterChange.bind(this))
      }
    })

    this.removeEventListener('click', this.handleActiveFilterClick.bind(this))

    window.removeEventListener('popstate', this.handlePopState.bind(this))
  }

  /**
   * Handle sorting changes from radio buttons
   */
  private async handleSortChange(event: Event) {
    const radio = event.currentTarget as HTMLInputElement
    const sortValue = radio.dataset.sortValue

    if (!sortValue) {
      return
    }

    if (this.isLoading || this.isUpdatingFromServer) {
      this.currentSortValue = sortValue
      this.hasQueuedUpdate = true
      return
    }

    this.currentSortValue = sortValue
    await this.applyFiltersAndSorting(true)
  }

  /**
   * Handle filter changes from checkboxes and range inputs
   */
  private async handleFilterChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const filterKey = input.dataset.filterKey

    if (!filterKey) {
      return
    }

    if (this.isLoading || this.isUpdatingFromServer) {
      this.hasQueuedUpdate = true
      this.updateCurrentFiltersFromDOM()
      return
    }

    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer)
    }

    if (input.type === 'range') {
      const container = input.closest('[data-id^="filter-"]')
      const minSlider = container?.querySelector('[data-price-slider="min"]') as HTMLInputElement
      const maxSlider = container?.querySelector('[data-price-slider="max"]') as HTMLInputElement

      if (minSlider && maxSlider) {
        const minValue = parseFloat(minSlider.value)
        const maxValue = parseFloat(maxSlider.value)
        const minDefault = parseFloat(minSlider.min)
        const maxDefault = parseFloat(maxSlider.max)

        if (minValue > minDefault || maxValue < maxDefault) {
          const minDollars = Math.round(minValue / 100)
          const maxDollars = Math.round(maxValue / 100)

          this.currentFilters[minSlider.dataset.filterKey!] = [String(minDollars)]
          this.currentFilters[maxSlider.dataset.filterKey!] = [String(maxDollars)]
        } else {
          delete this.currentFilters[minSlider.dataset.filterKey!]
          delete this.currentFilters[maxSlider.dataset.filterKey!]
        }
      } else {
        const value = input.value
        if (value && value !== input.min) {
          this.currentFilters[filterKey] = [value]
        } else {
          delete this.currentFilters[filterKey]
        }
      }
    } else if (input.type === 'checkbox') {
      const filterValue = input.dataset.filterValue

      if (!filterValue) {
        return
      }

      if (input.checked) {
        if (!this.currentFilters[filterKey]) {
          this.currentFilters[filterKey] = []
        }
        if (!this.currentFilters[filterKey].includes(filterValue)) {
          this.currentFilters[filterKey].push(filterValue)
        }
      } else {
        if (this.currentFilters[filterKey]) {
          this.currentFilters[filterKey] = this.currentFilters[filterKey].filter(v => v !== filterValue)
          if (this.currentFilters[filterKey].length === 0) {
            delete this.currentFilters[filterKey]
          }
        }
      }
    }

    this.debounceTimer = window.setTimeout(async () => {
      try {
        await this.applyFiltersAndSorting(true)
      } catch {
        this.isLoading = false
      }
    }, input.type === 'range' ? 300 : 50)
  }

  /**
   * Handle clicks on active filter removal buttons
   */
  private async handleActiveFilterClick(event: Event) {
    const target = event.target as HTMLElement
    const button = target.closest('[data-remove-filter], [data-remove-price-filter], [data-clear-all-filters]') as HTMLElement

    if (!button) return

    event.preventDefault()
    event.stopPropagation()

    if (button.hasAttribute('data-clear-all-filters')) {
      this.currentFilters = {}
      this.clearAllUniversalSizeButtons()
      this.updateActiveFiltersVisibility()
      await this.applyFiltersAndSorting(true)
      this.updateUniversalSizeGridVisibility()
    } else if (button.hasAttribute('data-remove-price-filter')) {
      const priceKeys = Object.keys(this.currentFilters).filter(key =>
        key.includes('price.gte') || key.includes('price.lte')
      )
      priceKeys.forEach(key => delete this.currentFilters[key])
      this.updateActiveFiltersVisibility()
      await this.applyFiltersAndSorting(true)
      this.updateUniversalSizeGridVisibility()
    } else if (button.hasAttribute('data-remove-filter')) {
      const filterKey = button.dataset.filterKey
      const filterValue = button.dataset.filterValue

      if (filterKey && filterValue) {
        if (this.currentFilters[filterKey]) {
          this.currentFilters[filterKey] = this.currentFilters[filterKey].filter(v => v !== filterValue)
          if (this.currentFilters[filterKey].length === 0) {
            delete this.currentFilters[filterKey]
          }
        }
        this.clearSpecificUniversalSizeButton(filterKey, filterValue)
        this.updateActiveFiltersVisibility()
        await this.applyFiltersAndSorting(true)
        this.updateUniversalSizeGridVisibility()
      }
    }
  }

  /**
   * Update filter state from DOM inputs
   */
  private updateCurrentFiltersFromDOM() {
    this.currentFilters = {}
    this.filterInputs?.forEach(input => {
      const filterKey = input.dataset.filterKey
      const filterValue = input.dataset.filterValue

      if (!filterKey) {
        return
      }

      if (input.type === 'checkbox') {
        if (input.checked && filterValue) {
          if (!this.currentFilters[filterKey]) {
            this.currentFilters[filterKey] = []
          }
          if (!this.currentFilters[filterKey].includes(filterValue)) {
            this.currentFilters[filterKey].push(filterValue)
          }
        }
      } else if (input.type === 'range') {
        const value = input.value
        if (value && value !== input.min) {
          this.currentFilters[filterKey] = [value]
        }
      }
    })
  }

  /**
   * Handle browser navigation (back/forward buttons)
   */
  private handlePopState() {
    const urlParams = new URLSearchParams(window.location.search)
    const sortBy = urlParams.get('sort_by') || 'manual'

    const newFilters: Record<string, string[]> = {}
    for (const [key, value] of urlParams.entries()) {
      if (key !== 'sort_by' && key !== 'q' && key !== 'page') {
        if (!newFilters[key]) {
          newFilters[key] = []
        }
        newFilters[key].push(value)
      }
    }

    const sortChanged = sortBy !== this.currentSortValue
    const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(this.currentFilters)

    if (sortChanged || filtersChanged) {
      this.currentSortValue = sortBy
      this.currentFilters = newFilters
      this.applyFiltersAndSorting(false)
    }
  }

  /**
   * Apply current sorting and filtering settings
   */
  private async applyFiltersAndSorting(updateHistory: boolean = true) {
    this.isLoading = true
    this.setFilterInputsDisabled(true)

    try {
      const url = this.buildFilteredURL()

      if (updateHistory) {
        window.history.pushState(null, '', url)
      }

      this.updateActiveButton(this.currentSortValue)
      this.updateActiveFilters()

      await this.fetchSortedContent(url)
    } catch {
    } finally {
      this.isLoading = false
      this.setFilterInputsDisabled(false)

      await this.processQueuedUpdates()
    }
  }

  /**
   * Enable/disable filter inputs during loading
   */
  private setFilterInputsDisabled(disabled: boolean) {
    this.filterInputs?.forEach(input => {
      input.disabled = disabled
    })
    this.sortButtons?.forEach(button => {
      button.disabled = disabled
    })

    if (disabled) {
      this.setAttribute('data-loading', '')
    } else {
      this.removeAttribute('data-loading')
    }
  }

  /**
   * Process any queued filter/sort updates
   */
  private async processQueuedUpdates() {
    if (this.hasQueuedUpdate && !this.isLoading) {
      this.hasQueuedUpdate = false
      setTimeout(async () => {
        if (!this.isLoading) {
          await this.applyFiltersAndSorting(true)
        }
      }, 100)
    }
  }

  /**
   * Build URL with current filter and sort parameters
   */
  private buildFilteredURL(): string {
    const url = new URL(window.location.href)

    if (this.context === 'search') {
      url.pathname = '/search'
    }

    const keysToDelete = Array.from(url.searchParams.keys()).filter(key => {
      if (key === 'sort_by') return true
      if (key === 'q' || key === 'page') return false

      return key.startsWith('filter.') ||
             key.startsWith('p.') ||
             key.startsWith('v.') ||
             key.startsWith('m.') ||
             key.includes('.')
    })
    keysToDelete.forEach(key => url.searchParams.delete(key))

    if (this.currentSortValue !== 'manual') {
      url.searchParams.set('sort_by', this.currentSortValue)
    }

    for (const [filterKey, values] of Object.entries(this.currentFilters)) {
      values.forEach(value => {
        url.searchParams.append(filterKey, value)
      })
    }

    return url.toString()
  }

  /**
   * Update active button visual state
   */
  private updateActiveButton(sortValue: string) {
    this.sortButtons?.forEach(input => {
      input.checked = input.dataset.sortValue === sortValue
    })

    // Update the sort button label
    const sortLabel = document.querySelectorAll('[data-sort-label]')
    const labelText = ProductGridElement.SORT_LABELS[sortValue] || 'Recommended'
    sortLabel.forEach(label => {
      label.textContent = `Sort: ${labelText}`
    })
  }

  /**
   * Update active filter visual states
   */
  private updateActiveFilters() {
    this.filterInputs?.forEach(input => {
      const filterKey = input.dataset.filterKey
      const filterValue = input.dataset.filterValue

      if (!filterKey) {
        return
      }

      if (input.type === 'checkbox') {
        const isActive = this.currentFilters[filterKey]?.includes(filterValue || '') || false
        input.checked = isActive
      } else if (input.type === 'range') {
        const storedValue = this.currentFilters[filterKey]?.[0]
        if (storedValue) {
          const urlDollars = parseFloat(storedValue)
          const sliderCents = urlDollars * 100
          input.value = sliderCents.toString()
        } else {
          const isMinSlider = input.dataset.priceSlider === 'min'
          const isMaxSlider = input.dataset.priceSlider === 'max'

          if (isMinSlider) {
            input.value = input.min
          } else if (isMaxSlider) {
            input.value = input.max
          }
        }
      }
    })
  }

  /**
   * Update filter counts from server response
   */
  private updateFilterCountsFromResponse(doc: Document) {
    this.isUpdatingFromServer = true

    try {
      const responseFilters = doc.querySelectorAll('[data-filter-input]')

      responseFilters.forEach(responseInput => {
        const responseFilterKey = (responseInput as HTMLInputElement).dataset.filterKey
        const responseFilterValue = (responseInput as HTMLInputElement).dataset.filterValue

        if (!responseFilterValue && responseFilterKey?.includes('price')) {
          return
        }

        const currentInput = this.querySelector(`[data-filter-key="${responseFilterKey}"][data-filter-value="${responseFilterValue}"]`) as HTMLInputElement
        if (currentInput && responseInput) {
          currentInput.checked = (responseInput as HTMLInputElement).checked
          this.updateFilterValueCount(currentInput, responseInput as HTMLInputElement)
        }
      })

      const responseUniversalButtons = doc.querySelectorAll('[data-size-button]')

      responseUniversalButtons.forEach(responseButton => {
        const responseFilterValue = (responseButton as HTMLElement).dataset.filterValue
        const responseSizeCount = (responseButton as HTMLElement).dataset.sizeCount

        if (responseFilterValue && responseSizeCount) {
          const currentButtons = this.querySelectorAll(`[data-size-button][data-filter-value="${responseFilterValue}"]`)
          currentButtons.forEach(button => {
            const buttonElement = button as HTMLElement
            buttonElement.dataset.sizeCount = responseSizeCount
          })
        }
      })

      this.updateActiveFilterCounts(doc)
    } catch {
    } finally {
      setTimeout(() => {
        this.isUpdatingFromServer = false
      }, 100)
    }
  }

  /**
   * Update count for individual filter values
   */
  private updateFilterValueCount(currentInput: HTMLInputElement, responseInput: HTMLInputElement) {
    try {
      const currentLabel = this.querySelector(`label[for="${currentInput.id}"]`)
      if (!currentLabel) return

      const responseLabel = responseInput.closest('div')?.querySelector(`label[for="${responseInput.id}"]`)
      if (!responseLabel) return

      const responseCountSpan = responseLabel.querySelector('[data-filter-value-count]')
      const currentCountSpan = currentLabel.querySelector('[data-filter-value-count]')

      if (responseCountSpan && currentCountSpan) {
        const newCount = responseCountSpan.textContent || '0'
        currentCountSpan.textContent = newCount

        const count = parseInt(newCount, 10)

        const filterKey = `${currentInput.dataset.filterKey}=${currentInput.dataset.filterValue}`

        if (count === 0 && !currentInput.checked) {
          this.disabledFilterStates.set(filterKey, true)
          currentInput.disabled = true
          currentInput.setAttribute('disabled', 'disabled')
        } else {
          this.disabledFilterStates.set(filterKey, false)
          currentInput.disabled = false
          currentInput.removeAttribute('disabled')
        }
      }
    } catch {
    }
  }

  /**
   * Reapply disabled states to filter inputs after DOM updates
   */
  private reapplyDisabledStates() {
    try {
      this.disabledFilterStates.forEach((shouldBeDisabled, filterKey) => {
        const [filterDataKey, filterValue] = filterKey.split('=')
        const input = this.querySelector(`[data-filter-key="${filterDataKey}"][data-filter-value="${filterValue}"]`) as HTMLInputElement

        if (input) {
          if (shouldBeDisabled && !input.checked) {
            input.disabled = true
            input.setAttribute('disabled', 'disabled')
          } else {
            input.disabled = false
            input.removeAttribute('disabled')
          }
        }
      })
    } catch {
    }
  }

  /**
   * Update active filter counts in headers
   */
  private updateActiveFilterCounts(doc: Document) {
    try {
      let activeFilterCount = 0
      let hasPriceFilters = false

      for (const [key, values] of Object.entries(this.currentFilters)) {
        if (key.includes('price.gte') || key.includes('price.lte')) {
          hasPriceFilters = true
        } else {
          activeFilterCount += values.length
        }
      }

      if (hasPriceFilters) {
        activeFilterCount += 1
      }

      const currentMainCount = this.querySelector('[data-filter-count-total]')

      if (currentMainCount) {
        currentMainCount.textContent = activeFilterCount.toString()
      }

      const responseSectionCounts = doc.querySelectorAll('[data-filter-section-count]')

      responseSectionCounts.forEach(responseCount => {
        const filterName = responseCount.getAttribute('data-filter-name')
        if (!filterName) return

        const currentCount = this.querySelector(`[data-filter-section-count][data-filter-name="${filterName}"]`)

        if (currentCount && responseCount.textContent !== currentCount.textContent) {
          currentCount.textContent = responseCount.textContent
        }
      })
    } catch {
    }
  }

  /**
   * Update product count display in toolbar
   */
  private updateProductCount(doc: Document) {
    try {
      const responseProductCount = doc.querySelector('[data-product-count]')
      const currentProductCount = document.querySelector('[data-product-count]')

      if (responseProductCount && currentProductCount) {
        currentProductCount.textContent = responseProductCount.textContent
      }

      // Update filter sidebar "View X Results" button
      this.updateFilterResultsButton(doc)

      if (this.context === 'search') {
        this.dispatchSearchResultsUpdate(doc)
      }
    } catch {
    }
  }

  /**
   * Update filter sidebar "View X Results" button
   */
  private updateFilterResultsButton(doc: Document) {
    try {
      // Count actual product cards in the response grid
      let count = 0
      const responseProductGrid = doc.querySelector('[data-product-grid]')
      if (responseProductGrid) {
        const productCards = responseProductGrid.querySelectorAll('[data-product-card]')
        count = productCards.length
      }

      const currentFilterResultsCount = document.querySelector('[data-filter-results-count]')
      const currentFilterResultsLabel = document.querySelector('[data-filter-results-label]')

      if (currentFilterResultsCount && currentFilterResultsLabel) {
        currentFilterResultsCount.textContent = count.toString()
        currentFilterResultsLabel.textContent = count === 1 ? 'Result' : 'Results'
      }
    } catch {
    }
  }

  /**
   * Dispatch search results update event for search header
   */
  private dispatchSearchResultsUpdate(doc: Document) {
    try {
      let resultsCount = 0

      // Count actual product cards in the response grid
      const responseProductGrid = doc.querySelector('[data-product-grid]')
      if (responseProductGrid) {
        const productCards = responseProductGrid.querySelectorAll('[data-product-card]')
        resultsCount = productCards.length
      }

      const urlParams = new URLSearchParams(window.location.search)
      const searchTerms = urlParams.get('q') || ''

      document.dispatchEvent(new CustomEvent('searchResultsUpdated', {
        detail: {
          resultsCount,
          searchTerms
        }
      }))
    } catch {
    }
  }

  /**
   * Update active filters section from server response
   */
  private updateActiveFiltersFromResponse(doc: Document) {
    try {
      const responseActiveFilters = doc.querySelector('[data-active-filters]')
      const currentActiveFilters = this.querySelector('[data-active-filters]')

      if (responseActiveFilters && currentActiveFilters) {
        currentActiveFilters.innerHTML = responseActiveFilters.innerHTML
      } else if (responseActiveFilters && !currentActiveFilters) {
        const filtersContainer = this.querySelector('[data-filters-list]')
        if (filtersContainer && filtersContainer.parentElement) {
          filtersContainer.parentElement.insertBefore(responseActiveFilters.cloneNode(true), filtersContainer)
        }
      } else if (!responseActiveFilters && currentActiveFilters) {
        currentActiveFilters.remove()
      }

      this.injectPriceRangeButton()
    } catch {
    }
  }

  /**
   * Manually inject price range button when both gte and lte filters are active
   */
  private injectPriceRangeButton() {
    try {
      const priceGte = this.currentFilters['filter.v.price.gte']
      const priceLte = this.currentFilters['filter.v.price.lte']

      if (priceGte || priceLte) {
        const minPrice = priceGte ? parseInt(priceGte[0]) : 0
        const maxPrice = priceLte ? parseInt(priceLte[0]) : 99999

        const minPriceFormatted = formatMoney(minPrice * 100, '${{amount_no_decimals}}')
        const maxPriceFormatted = formatMoney(maxPrice * 100, '${{amount_no_decimals}}')

        const priceRangeButton = `
          <button
            class="shrink-0 btn bg-neutral-light btn-xs text-xs !font-450 gap-x-0.5 rounded-[80px]"
            data-remove-price-filter
            aria-label="Remove price range filter"
          >
            ${minPriceFormatted} - ${maxPriceFormatted}
            <div class="flex size-[9px] children:size-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.93908 5.94445L12.4724 0.466797L13.5276 1.5328L8.00514 6.9998L13.5276 12.4668L12.4724 13.5328L6.93908 8.05514L1.52764 13.4122L0.472351 12.3462L5.87302 6.9998L0.472353 1.65341L1.52764 0.587402L6.93908 5.94445Z" fill="currentColor"/>
              </svg>
            </div>
          </button>`

        const activeFiltersContainer = this.querySelector('[data-active-filters]')

        if (activeFiltersContainer) {
          const existingPriceButton = activeFiltersContainer.querySelector('[data-remove-price-filter]')
          if (existingPriceButton) {
            existingPriceButton.remove()
          }
          activeFiltersContainer.insertAdjacentHTML('beforeend', priceRangeButton)
          activeFiltersContainer.classList.remove('hidden')
        }
      } else {
        const existingPriceButton = this.querySelector('[data-remove-price-filter]')
        if (existingPriceButton) {
          existingPriceButton.remove()
        }
        this.updateActiveFiltersVisibility()
      }
    } catch {
    }
  }

  /**
   * Update visibility of active filters section based on current filters
   */
  private updateActiveFiltersVisibility() {
    try {
      const activeFiltersContainer = this.querySelector('[data-active-filters]')
      if (!activeFiltersContainer) return

      const hasNonPriceFilters = Object.keys(this.currentFilters).some(key =>
        !key.includes('price.gte') && !key.includes('price.lte') && this.currentFilters[key].length > 0
      )

      const hasPriceFilters = this.currentFilters['filter.v.price.gte'] || this.currentFilters['filter.v.price.lte']

      if (hasNonPriceFilters || hasPriceFilters) {
        activeFiltersContainer.classList.remove('hidden')
      } else {
        activeFiltersContainer.classList.add('hidden')
      }
    } catch {
    }
  }

  /**
   * Fetch and apply filtered content from server
   */
  private async fetchSortedContent(url: string): Promise<void> {
    const response = await fetch(url, {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const html = await response.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const responseProductGrid = doc.querySelector('x-product-grid')
    let newProductGridContainer: Element | null = null

    if (responseProductGrid) {
      const gridElement = responseProductGrid.querySelector('[data-product-grid]')
      newProductGridContainer = gridElement?.parentElement || null
    }

    if (newProductGridContainer && this.gridContainer) {
      const currentProductGrid = this.gridContainer.querySelector('[data-product-grid]')

      if (currentProductGrid) {
        const newProductGrid = newProductGridContainer.querySelector('[data-product-grid]')
        if (newProductGrid) {
          currentProductGrid.innerHTML = newProductGrid.innerHTML
        }
      }

      this.updateProductCount(doc)
      this.updateActiveFiltersFromResponse(doc)
      this.updateActiveFiltersVisibility()
      this.updateActiveFiltersWithPreferredSizes()
      this.reinitializeLazyLoading()
      this.initializePriceSliders()
      this.initializeUniversalSizeFilters()

      this.updateFilterCountsFromResponse(doc)
      this.updateUniversalSizeGridVisibility()

      setTimeout(() => {
        this.reapplyDisabledStates()
      }, 10)

      this.dispatchEvent(new CustomEvent('product-grid:updated', {
        detail: { sortBy: this.currentSortValue },
        bubbles: true
      }))
    }
  }

  /**
   * Reinitialize lazy loading for new images
   */
  private reinitializeLazyLoading() {
    if ((window as any).lazyLoadInstance) {
      (window as any).lazyLoadInstance.update()
    }
  }

  /**
   * Initialize price range sliders
   */
  private initializePriceSliders() {
    const priceSliders = this.querySelectorAll('[data-price-slider]')

    priceSliders.forEach((slider) => {
      const input = slider as HTMLInputElement
      const container = input.closest('[data-id^="filter-"]')
      if (!container) return

      const isMinSlider = input.dataset.priceSlider === 'min'
      const isMaxSlider = input.dataset.priceSlider === 'max'

      const filterKey = input.dataset.filterKey
      if (filterKey && this.currentFilters[filterKey]) {
        const urlDollars = parseFloat(this.currentFilters[filterKey][0])
        const sliderCents = urlDollars * 100
        input.value = sliderCents.toString()
      } else {
        if (isMinSlider) {
          input.value = input.min
        } else if (isMaxSlider) {
          input.value = input.max
        }
      }

      input.addEventListener('input', (e) => {
        this.handleFilterChange(e)
      })
    })

    const containers = new Set<HTMLElement>()
    priceSliders.forEach((slider) => {
      const container = (slider as HTMLElement).closest('[data-id^="filter-"]') as HTMLElement
      if (container) containers.add(container)
    })

    containers.forEach(container => {
      this.updateSliderVisuals(container)
      this.updatePriceDisplays(container)
      this.setupMouseInteraction(container)
    })
  }

  /**
   * Update price display labels
   */
  private updatePriceDisplays(container: HTMLElement) {
    const minSlider = container.querySelector('[data-price-slider="min"]') as HTMLInputElement
    const maxSlider = container.querySelector('[data-price-slider="max"]') as HTMLInputElement
    const minDisplay = container.querySelector('[data-price-display="min"]') as HTMLElement
    const maxDisplay = container.querySelector('[data-price-display="max"]') as HTMLElement

    if (!minSlider || !maxSlider || !minDisplay || !maxDisplay) return

    const minValue = parseInt(minSlider.value)
    const maxValue = parseInt(maxSlider.value)

    minDisplay.textContent = formatMoney(minValue, '${{amount_no_decimals}}')
    maxDisplay.textContent = formatMoney(maxValue, '${{amount_no_decimals}}')
  }

  /**
   * Setup mouse interaction for price slider thumbs
   */
  private setupMouseInteraction(container: HTMLElement) {
    const minThumb = container.querySelector('[data-thumb="min"]') as HTMLElement
    const maxThumb = container.querySelector('[data-thumb="max"]') as HTMLElement
    const trackContainer = container.querySelector('[data-price-slider-track]') as HTMLElement
    const minSlider = container.querySelector('[data-price-slider="min"]') as HTMLInputElement
    const maxSlider = container.querySelector('[data-price-slider="max"]') as HTMLInputElement

    if (!trackContainer || !minSlider || !maxSlider || !minThumb || !maxThumb) return

    let isDragging = false
    let activeSlider: 'min' | 'max' | null = null

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging || !activeSlider) return

      const rect = trackContainer.getBoundingClientRect()
      const percentage = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))

      const min = parseFloat(minSlider.min)
      const max = parseFloat(minSlider.max)
      const step = parseFloat(minSlider.step)
      const newValue = Math.round((min + percentage * (max - min)) / step) * step

      if (activeSlider === 'min') {
        const currentMaxValue = parseFloat(maxSlider.value)
        const constrainedValue = Math.max(min, Math.min(newValue, currentMaxValue - step))
        minSlider.value = constrainedValue.toString()
      } else {
        const currentMinValue = parseFloat(minSlider.value)
        const constrainedValue = Math.min(max, Math.max(newValue, currentMinValue + step))
        maxSlider.value = constrainedValue.toString()
      }

      this.updateSliderVisuals(container as HTMLElement)
      this.updatePriceDisplays(container as HTMLElement)
    }

    const handleMouseUp = () => {
      if (isDragging && activeSlider) {
        const inputEvent = new Event('input', { bubbles: true })
        if (activeSlider === 'min') {
          minSlider.dispatchEvent(inputEvent)
        } else {
          maxSlider.dispatchEvent(inputEvent)
        }
      }

      isDragging = false
      activeSlider = null
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    minThumb.addEventListener('mousedown', (event) => {
      event.preventDefault()
      event.stopPropagation()
      activeSlider = 'min'
      isDragging = true
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    })

    maxThumb.addEventListener('mousedown', (event) => {
      event.preventDefault()
      event.stopPropagation()
      activeSlider = 'max'
      isDragging = true
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    })

    trackContainer.addEventListener('mousedown', (event) => {
      const mouseEvent = event as MouseEvent

      const clickedElement = mouseEvent.target as HTMLElement
      if (clickedElement === minThumb || clickedElement === maxThumb ||
          clickedElement.hasAttribute('data-thumb')) {
        return
      }

      const rect = trackContainer.getBoundingClientRect()
      const percentage = (mouseEvent.clientX - rect.left) / rect.width

      const minValue = parseFloat(minSlider.value)
      const maxValue = parseFloat(maxSlider.value)
      const min = parseFloat(minSlider.min)
      const max = parseFloat(minSlider.max)

      const minPos = (minValue - min) / (max - min)
      const maxPos = (maxValue - min) / (max - min)

      if (percentage < (minPos + maxPos) / 2) {
        activeSlider = 'min'
      } else {
        activeSlider = 'max'
      }

      isDragging = true
      handleMouseMove(mouseEvent)
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    })
  }

  /**
   * Update visual positions of slider elements
   */
  private updateSliderVisuals(container: HTMLElement) {
    const minSlider = container.querySelector('[data-price-slider="min"]') as HTMLInputElement
    const maxSlider = container.querySelector('[data-price-slider="max"]') as HTMLInputElement
    const minThumb = container.querySelector('[data-thumb="min"]') as HTMLElement
    const maxThumb = container.querySelector('[data-thumb="max"]') as HTMLElement
    const trackFill = container.querySelector('[data-track-fill]') as HTMLElement

    if (!minSlider || !maxSlider || !minThumb || !maxThumb || !trackFill) {
      return
    }

    const min = parseFloat(minSlider.min)
    const max = parseFloat(minSlider.max)
    const minValue = parseFloat(minSlider.value)
    const maxValue = parseFloat(maxSlider.value)

    if (min === max || max <= min) {
      minThumb.style.left = '0%'
      maxThumb.style.left = '100%'
      trackFill.style.left = '0%'
      trackFill.style.right = '0%'
      return
    }

    const minPos = ((minValue - min) / (max - min)) * 100
    const maxPos = ((maxValue - min) / (max - min)) * 100

    minThumb.style.left = `${minPos}%`
    maxThumb.style.left = `${maxPos}%`

    trackFill.style.left = `${minPos}%`
    trackFill.style.right = `${100 - maxPos}%`
  }

  /**
   * Initialize universal size filter interactions
   */
  private initializeUniversalSizeFilters() {
    const universalSizeFilters = this.querySelectorAll('[data-universal-size-filter]')

    universalSizeFilters.forEach(filter => {
      this.setupUniversalSizeFilter(filter as HTMLElement)
    })
  }

  /**
   * Setup interactions for a single universal size filter
   */
  private setupUniversalSizeFilter(filterElement: HTMLElement) {
    // Remove any existing delegated listeners to prevent duplicates
    const oldListener = (filterElement as any)._sizeFilterListener
    if (oldListener) {
      filterElement.removeEventListener('click', oldListener)
    }

    // Create new delegated event listener
    const listener = (event: Event) => {
      const target = event.target as HTMLElement

      // Handle size type tab clicks
      const sizeTypeTab = target.closest('[data-size-type-tab]') as HTMLElement
      if (sizeTypeTab) {
        event.preventDefault()
        event.stopPropagation()
        this.switchSizeTypeTab(filterElement, sizeTypeTab)
        return
      }

      // Handle size button clicks
      const sizeButton = target.closest('[data-size-button]') as HTMLElement
      if (sizeButton) {
        event.preventDefault()
        event.stopPropagation()
        this.handleSizeButtonClick(sizeButton)
      }
    }

    // Store reference to listener so we can remove it later
    ;(filterElement as any)._sizeFilterListener = listener

    // Add single delegated listener to the filter element
    filterElement.addEventListener('click', listener)
  }

  /**
   * Switch active gender tab
   */
  private switchGenderTab(filterElement: HTMLElement, clickedTab: HTMLElement) {
    const gender = clickedTab.dataset.genderTab
    if (!gender) return

    // Update tab active states
    const genderTabs = filterElement.querySelectorAll('[data-gender-tab]')
    genderTabs.forEach(tab => {
      const tabElement = tab as HTMLElement
      if (tabElement === clickedTab) {
        tabElement.classList.add('bg-brand-white')
        tabElement.dataset.active = 'true'
      } else {
        tabElement.classList.remove('bg-brand-white')
        tabElement.dataset.active = 'false'
      }
    })

    // Show/hide appropriate size grids
    this.updateVisibleSizeGrids(filterElement)
  }

  /**
   * Switch active size type tab
   */
  private switchSizeTypeTab(filterElement: HTMLElement, clickedTab: HTMLElement) {
    const sizeType = clickedTab.dataset.sizeTypeTab
    if (!sizeType) return

    // Update tab active states
    const sizeTypeTabs = filterElement.querySelectorAll('[data-size-type-tab]')
    sizeTypeTabs.forEach(tab => {
      const tabElement = tab as HTMLElement
      if (tabElement === clickedTab) {
        tabElement.classList.add('bg-white')
        tabElement.dataset.active = 'true'
      } else {
        tabElement.classList.remove('bg-white')
        tabElement.dataset.active = 'false'
      }
    })

    // Store preference in localStorage (only for valid universal size types)
    if (['EU', 'UK', 'US'].includes(sizeType)) {
      localStorage.setItem('preferred_size_range', sizeType)
    }

    // Update active filters to show preferred size format
    this.updateActiveFiltersWithPreferredSizes()

    // Show/hide appropriate size grids
    this.updateVisibleSizeGrids(filterElement)

    // Mark equivalent sizes as selected in the new tab
    try {
      this.markEquivalentSizesAsSelected(filterElement)
    } catch (error) {
      console.error('Error marking equivalent sizes:', error)
    }
  }

  /**
   * Update which size grids are visible based on active tabs
   */
  private updateVisibleSizeGrids(filterElement: HTMLElement) {
    const activeSizeTypeTab = filterElement.querySelector('[data-size-type-tab][data-active="true"]') as HTMLElement
    const activeSizeType = activeSizeTypeTab?.dataset.sizeTypeTab

    // Show/hide size grids based on active size type (no gender in simplified version)
    const sizeGrids = filterElement.querySelectorAll('[data-size-grid]')
    sizeGrids.forEach(grid => {
      const gridElement = grid as HTMLElement
      const gridSizeType = gridElement.dataset.sizeType

      if (gridSizeType === activeSizeType) {
        gridElement.style.display = 'flex'
      } else {
        gridElement.style.display = 'none'
      }
    })
  }

  /**
   * Clear all universal size buttons visual state
   */
  private clearAllUniversalSizeButtons() {
    const universalSizeButtons = this.querySelectorAll('[data-size-button]')
    universalSizeButtons.forEach(button => {
      const buttonElement = button as HTMLElement
      const checkbox = buttonElement.querySelector('input[type="checkbox"]') as HTMLInputElement

      if (checkbox) {
        checkbox.checked = false
        buttonElement.classList.remove('btn-fill', '!font-medium')
        buttonElement.classList.add('!font-450', 'bg-brand-size-selector')
      }
    })
  }

  /**
   * Clear visual state for a specific universal size button
   */
  private clearSpecificUniversalSizeButton(filterKey: string, filterValue: string) {
    const universalSizeButtons = this.querySelectorAll(
      `[data-size-button][data-filter-value="${filterValue}"]`
    )

    universalSizeButtons.forEach(button => {
      const universalSizeButton = button as HTMLElement
      const checkbox = universalSizeButton.querySelector('input[type="checkbox"]') as HTMLInputElement

      if (checkbox && checkbox.dataset.filterKey === filterKey) {
        checkbox.checked = false
        universalSizeButton.classList.remove('btn-fill', '!font-medium')
        universalSizeButton.classList.add('!font-450', 'bg-brand-size-selector')
      }
    })
  }

  /**
   * Handle size button clicks
   */
  private handleSizeButtonClick(buttonElement: HTMLElement) {
    const filterValue = buttonElement.dataset.filterValue
    if (!filterValue) return

    // Check cooldown period to prevent rapid clicks causing race conditions
    const now = Date.now()
    const timeSinceLastClick = now - this.lastSizeButtonClickTime

    if (timeSinceLastClick < this.sizeButtonCooldown) {
      // Still in cooldown period - ignore this click
      return
    }

    // Update last click time
    this.lastSizeButtonClickTime = now

    // Find the hidden checkbox input
    const checkbox = buttonElement.querySelector('input[type="checkbox"]') as HTMLInputElement
    if (!checkbox) return

    // Toggle checkbox state
    const wasChecked = checkbox.checked
    checkbox.checked = !wasChecked

    // Update button visual state immediately
    if (checkbox.checked) {
      buttonElement.classList.add('btn-fill', '!font-medium')
      buttonElement.classList.remove('!font-450', 'bg-brand-size-selector', 'btn-border')
    } else {
      buttonElement.classList.remove('btn-fill', '!font-medium')
      buttonElement.classList.add('!font-450', 'bg-brand-size-selector')
    }

    // Update equivalent sizes in other tabs
    this.updateEquivalentSizesInOtherTabs(buttonElement, checkbox.checked)

    // Trigger filter change event
    const changeEvent = new Event('change', { bubbles: true })
    checkbox.dispatchEvent(changeEvent)
  }

  /**
   * Get current sort value (public API)
   */
  public getCurrentSort(): string {
    return this.currentSortValue
  }

  /**
   * Get current filter values (public API)
   */
  public getCurrentFilters(): Record<string, string[]> {
    return { ...this.currentFilters }
  }

  /**
   * Update active filters to show preferred size format
   */
  private updateActiveFiltersWithPreferredSizes() {
    const activeFiltersContainer = document.querySelector('[data-active-filters]')
    if (!activeFiltersContainer) return

    const sizeFilterButtons = activeFiltersContainer.querySelectorAll('[data-remove-filter]')

    sizeFilterButtons.forEach(button => {
      const buttonElement = button as HTMLElement
      const filterKey = buttonElement.dataset.filterKey
      const originalLabel = buttonElement.dataset.originalLabel

      // Check if this is a universal size filter with original label stored
      if (filterKey && filterKey.includes('size') && originalLabel && this.isUniversalSize(originalLabel)) {
        // Find the text node (first child that's text, before the close icon)
        const buttonTextSpan = buttonElement.childNodes[0]
        if (buttonTextSpan && buttonTextSpan.nodeType === Node.TEXT_NODE) {
          const formattedLabel = this.formatUniversalSizeValue(originalLabel)
          buttonTextSpan.textContent = formattedLabel
        }
      }
    })
  }

  /**
   * Check if a size value is universal (contains multiple formats)
   */
  private isUniversalSize(value: string): boolean {
    return value.includes('/') || value.includes(',')
  }

  /**
   * Format universal size value to show preferred size type
   */
  private formatUniversalSizeValue(value: string): string {
    if (!this.isUniversalSize(value)) {
      return value
    }

    const preferredSizeRange = localStorage.getItem('preferred_size_range') || 'EU'
    const sizesByGender = this.parseSizeString(value)

    if (!sizesByGender) {
      return value
    }

    const sizeValues: string[] = []

    Object.entries(sizesByGender).forEach(([, sizesByType]) => {
      if (sizesByType[preferredSizeRange]) {
        const displaySize = sizesByType[preferredSizeRange]
        sizeValues.push(displaySize.toString())
      }
    })

    if (sizeValues.length > 0) {
      const sizeTypeLabel = preferredSizeRange === 'EU' ? 'EU' : preferredSizeRange
      return `${sizeTypeLabel} ${sizeValues[0]}`
    }

    return value
  }

  /**
   * Parse universal size string into structured data
   */
  private parseSizeString(sizeString: string): Record<string, Record<string, number>> | null {
    const sizingArray = sizeString.includes('/') ? sizeString.split('/') : sizeString.split(',')
    const sizesByGender: Record<string, Record<string, number>> = {}
    let euroSize: number | null = null

    sizingArray.forEach(val => {
      const cleanVal = val.trim()

      if (cleanVal.includes('WOMENS') || cleanVal.includes('MENS') || cleanVal.includes('KIDS')) {
        const parts = cleanVal.split(' ')
        if (parts.length >= 3) {
          const gender = parts[0]
          const sizeType = parts[1]
          const sizeValue = parseFloat(parts[2])

          if (!isNaN(sizeValue) && sizeValue > 0) {
            if (!sizesByGender[gender]) {
              sizesByGender[gender] = {}
            }
            sizesByGender[gender][sizeType] = sizeValue
          }
        }
      } else if (cleanVal.startsWith('EURO ') || cleanVal.startsWith('EU ')) {
        const parts = cleanVal.split(' ')
        if (parts.length >= 2) {
          const sizeValue = parseFloat(parts[1])
          if (!isNaN(sizeValue) && sizeValue > 0) {
            euroSize = sizeValue
          }
        }
      } else if (cleanVal.includes('EURO') || cleanVal.includes('EU')) {
        // Handle cases like "EURO21.5" or "EU21.5" without spaces
        const euroMatch = cleanVal.match(/(EURO|EU)(\d+\.?\d*)/i)
        if (euroMatch) {
          const sizeValue = parseFloat(euroMatch[2])
          if (!isNaN(sizeValue) && sizeValue > 0) {
            euroSize = sizeValue
          }
        }
      }
    })

    // Add Euro size to all genders if found
    if (euroSize && Object.keys(sizesByGender).length > 0) {
      Object.keys(sizesByGender).forEach(gender => {
        sizesByGender[gender].EU = euroSize!
      })
    }

    return Object.keys(sizesByGender).length > 0 ? sizesByGender : null
  }

  /**
   * Update universal size grid visibility based on current filter state
   */
  private updateUniversalSizeGridVisibility() {
    const universalSizeFilters = this.querySelectorAll('[data-universal-size-filter]')

    universalSizeFilters.forEach(filterElement => {
      const universalSizeButtons = filterElement.querySelectorAll('[data-size-button]')

      universalSizeButtons.forEach(button => {
        const buttonElement = button as HTMLElement
        const input = buttonElement.querySelector('input') as HTMLInputElement

        if (buttonElement.hasAttribute('data-size-count')) {
          const currentCount = parseInt(buttonElement.dataset.sizeCount || '0')

          if (currentCount === 0) {
            buttonElement.classList.add('hidden')
            buttonElement.setAttribute('disabled', 'true')
            if (input) {
              input.setAttribute('disabled', 'true')
            }
          } else {
            buttonElement.classList.remove('hidden')
            buttonElement.removeAttribute('disabled')
            if (input) {
              input.removeAttribute('disabled')
            }
          }
        }
      })
    })
  }

  /**
   * Update equivalent sizes in other tabs when a size is clicked
   */
  private updateEquivalentSizesInOtherTabs(clickedButton: HTMLElement, isChecked: boolean) {
    const filterValue = clickedButton.dataset.filterValue
    if (!filterValue) return

    // Find the universal size filter container
    const universalSizeFilter = clickedButton.closest('[data-universal-size-filter]') as HTMLElement
    if (!universalSizeFilter) return

    // Find all other size grids (not currently visible)
    const allSizeGrids = universalSizeFilter.querySelectorAll('[data-size-grid]')

    allSizeGrids.forEach(grid => {
      const gridElement = grid as HTMLElement

      // Skip the currently visible grid
      if (gridElement.style.display === 'flex') return

      // Find the equivalent button in this grid
      const equivalentButton = gridElement.querySelector(`[data-size-button][data-filter-value="${filterValue}"]`) as HTMLElement
      if (equivalentButton) {
        const checkbox = equivalentButton.querySelector('input[type="checkbox"]') as HTMLInputElement
        if (checkbox) {
          // Update the state to match the clicked button
          checkbox.checked = isChecked
          if (isChecked) {
            equivalentButton.classList.add('btn-fill', '!font-medium')
            equivalentButton.classList.remove('btn-border', '!font-450', 'bg-brand-size-selector')
          } else {
            equivalentButton.classList.remove('btn-fill', '!font-medium')
            equivalentButton.classList.add('!font-450', 'bg-brand-size-selector')
          }
        }
      }
    })
  }

  /**
   * Mark equivalent sizes as selected when switching tabs
   */
  private markEquivalentSizesAsSelected(filterElement: HTMLElement) {
    const filterParam = filterElement.dataset.filterParam
    if (!filterParam || !this.currentFilters[filterParam]) return

    // Get currently selected size values
    const selectedSizes = this.currentFilters[filterParam]

    // Find the currently visible size grid
    const visibleGrid = filterElement.querySelector('[data-size-grid][style*="flex"]') as HTMLElement
    if (!visibleGrid) return

    // Clear all active states in the visible grid first
    const allButtons = visibleGrid.querySelectorAll('[data-size-button]')
    allButtons.forEach(button => {
      const buttonElement = button as HTMLElement
      buttonElement.classList.remove('btn-fill', '!font-medium')
      buttonElement.classList.add('!font-450', 'bg-brand-size-selector')
      const checkbox = buttonElement.querySelector('input[type="checkbox"]') as HTMLInputElement
      if (checkbox) {
        checkbox.checked = false
      }
    })

    // Mark buttons as active if their filter value is in selectedSizes
    selectedSizes.forEach(selectedValue => {
      const matchingButton = visibleGrid.querySelector(`[data-size-button][data-filter-value="${selectedValue}"]`) as HTMLElement
      if (matchingButton) {
        matchingButton.classList.add('btn-fill', '!font-medium')
        matchingButton.classList.remove('btn-border', '!font-450', 'bg-brand-size-selector')
        const checkbox = matchingButton.querySelector('input[type="checkbox"]') as HTMLInputElement
        if (checkbox) {
          checkbox.checked = true
        }
      }
    })
  }
}
